import { useState, useCallback, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import { debug } from '@/shared/lib';

import { notificationModel } from '@/entities/notification';

import {
  ConsultationNode,
  COPY_SUCCESS_DURATION,
  dentalConsultationMessages,
} from '../config';
import { formatConsultationCopyText } from '../lib';

type UseConsultationEditParams = {
  initialData: ConsultationNode[];
  onSave?: (data: ConsultationNode[]) => Promise<void>;
};

type UseConsultationEditReturn = {
  isEditing: boolean;
  editedData: ConsultationNode[];
  showCopySuccess: boolean;
  handleEdit: () => void;
  handleCopy: () => Promise<void>;
  handleTextChange: (
    nodeIndex: number,
    sectionIndex: number,
    contentIndex: number,
    newText: string,
  ) => void;
};

export const useConsultationEdit = ({
  initialData,
  onSave,
}: UseConsultationEditParams): UseConsultationEditReturn => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [editedData, setEditedData] = useState(initialData);

  const editedDataRef = useRef(editedData);
  editedDataRef.current = editedData;

  useEffect(() => {
    if (isEditing) {
      return;
    }
    const isSameContent =
      JSON.stringify(editedDataRef.current) === JSON.stringify(initialData);
    if (!isSameContent) {
      setEditedData(initialData);
    }
  }, [initialData, isEditing]);

  const handleEdit = useCallback(async () => {
    if (isEditing) {
      // Save logic
      if (onSave) {
        try {
          await onSave(editedData);
          setIsEditing(false);
          debug('Consultation data saved successfully');
        } catch (error) {
          debug('Error saving consultation data:', error);
          dispatch(
            notificationModel.actions.addNotification({
              id: Date.now().toString(),
              type: 'error',
              message: error instanceof Error ? error.message : 'Save failed',
            }),
          );
        }
      } else {
        setIsEditing(false);
      }
    } else {
      setIsEditing(true);
    }
  }, [isEditing, editedData, onSave]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(
        formatConsultationCopyText(editedData),
      );
      setShowCopySuccess(true);
      setTimeout(() => {
        setShowCopySuccess(false);
      }, COPY_SUCCESS_DURATION);
    } catch (err) {
      debug('Error copying to clipboard:', err);
      dispatch(
        notificationModel.actions.addNotification({
          id: Date.now().toString(),
          type: 'error',
          message: formatMessage(dentalConsultationMessages.copyError),
        }),
      );
    }
  }, [editedData, formatMessage]);

  const handleTextChange = useCallback(
    (
      nodeIndex: number,
      sectionIndex: number,
      contentIndex: number,
      newText: string,
    ) => {
      setEditedData((prevData) => {
        const newData = [...prevData];
        const node = { ...newData[nodeIndex] };
        const section = { ...node.children[sectionIndex] };
        const content = [...section.content];

        // If content array is empty, create a new content item
        if (content.length === 0) {
          content.push({ text: newText });
        } else {
          content[contentIndex] = { ...content[contentIndex], text: newText };
        }

        section.content = content;
        node.children = [...node.children];
        node.children[sectionIndex] = section;
        newData[nodeIndex] = node;
        return newData;
      });
    },
    [],
  );

  return {
    isEditing,
    editedData,
    showCopySuccess,
    handleEdit,
    handleCopy,
    handleTextChange,
  };
};
